import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { apiService } from '../../services/api'
import { 
  ArrowRight, 
  ShoppingCart, 
  FileText, 
  Truck, 
  CreditCard, 
  Star,
  CheckCircle,
  Clock,
  AlertCircle,
  Play
} from 'lucide-react'

interface NextStep {
  id: string
  title: string
  description: string
  action: string
  route: string
  icon: React.ReactNode
  priority: 'high' | 'medium' | 'low'
  status: 'pending' | 'in-progress' | 'completed'
}

interface NextStepGuideProps {
  userRole: 'BUYER' | 'VENDOR'
}

const NextStepGuide: React.FC<NextStepGuideProps> = ({ userRole }) => {
  const navigate = useNavigate()
  // Remove unused user variable
  const [nextSteps, setNextSteps] = useState<NextStep[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadNextSteps()
  }, [userRole])

  const loadNextSteps = async () => {
    try {
      setLoading(true)
      
      // Get current workflow status
      const [poStats, agreementStats, fulfillmentStats, invoiceStats] = await Promise.all([
        apiService.get('/api/post-quote-activity/purchase-orders/statistics'),
        apiService.get('/api/post-quote-activity/digital-agreements/statistics'),
        apiService.get('/api/post-quote-activity/fulfillments/statistics'),
        apiService.get('/api/post-quote-activity/invoices/statistics')
      ])

      const steps: NextStep[] = []

      // Determine next steps based on user role and current status
      if (userRole === 'BUYER') {
        // Buyer workflow steps
        if ((poStats as any).data?.purchaseOrders?.pending > 0) {
          steps.push({
            id: 'review-po',
            title: 'Review Pending Purchase Orders',
            description: 'You have pending purchase orders that need your attention',
            action: 'Review Orders',
            route: '/enterprise/post-quote-activity/purchase-orders',
            icon: <ShoppingCart className="w-5 h-5" />,
            priority: 'high',
            status: 'pending'
          })
        }

        if ((agreementStats as any).data?.agreements?.pending > 0) {
          steps.push({
            id: 'sign-agreement',
            title: 'Sign Digital Agreements',
            description: 'Digital agreements are waiting for your signature',
            action: 'Sign Now',
            route: '/enterprise/post-quote-activity/agreements',
            icon: <FileText className="w-5 h-5" />,
            priority: 'high',
            status: 'pending'
          })
        }

        if ((fulfillmentStats as any).data?.fulfillments?.in_transit > 0) {
          steps.push({
            id: 'confirm-delivery',
            title: 'Confirm Delivery',
            description: 'Shipments are in transit and ready for delivery confirmation',
            action: 'Confirm Delivery',
            route: '/enterprise/post-quote-activity/fulfillments',
            icon: <Truck className="w-5 h-5" />,
            priority: 'medium',
            status: 'in-progress'
          })
        }

        if ((invoiceStats as any).data?.invoices?.pending > 0) {
          steps.push({
            id: 'process-payment',
            title: 'Process Invoice Payments',
            description: 'Invoices are pending payment',
            action: 'Pay Now',
            route: '/enterprise/post-quote-activity/invoices',
            icon: <CreditCard className="w-5 h-5" />,
            priority: 'medium',
            status: 'pending'
          })
        }

        if ((fulfillmentStats as any).data?.fulfillments?.delivered > 0) {
          steps.push({
            id: 'submit-feedback',
            title: 'Submit Vendor Feedback',
            description: 'Rate and review vendors for completed orders',
            action: 'Submit Feedback',
            route: '/enterprise/post-quote-activity/feedback',
            icon: <Star className="w-5 h-5" />,
            priority: 'low',
            status: 'pending'
          })
        }
      } else {
        // Vendor workflow steps
        if ((poStats as any).data?.purchaseOrders?.accepted > 0) {
          steps.push({
            id: 'create-agreement',
            title: 'Upload Digital Agreements',
            description: 'Upload contracts and agreements for accepted purchase orders',
            action: 'Upload Agreement',
            route: '/enterprise/post-quote-activity/agreements',
            icon: <FileText className="w-5 h-5" />,
            priority: 'high',
            status: 'pending'
          })
        }

        if ((fulfillmentStats as any).data?.fulfillments?.pending > 0) {
          steps.push({
            id: 'update-fulfillment',
            title: 'Update Fulfillment Details',
            description: 'Add tracking information and update shipping status',
            action: 'Update Fulfillment',
            route: '/enterprise/post-quote-activity/fulfillments',
            icon: <Truck className="w-5 h-5" />,
            priority: 'high',
            status: 'pending'
          })
        }

        if ((fulfillmentStats as any).data?.fulfillments?.shipped > 0) {
          steps.push({
            id: 'track-shipment',
            title: 'Track Shipments',
            description: 'Monitor shipment progress and update delivery status',
            action: 'Track Now',
            route: '/enterprise/post-quote-activity/fulfillments',
            icon: <Truck className="w-5 h-5" />,
            priority: 'medium',
            status: 'in-progress'
          })
        }

        if ((fulfillmentStats as any).data?.fulfillments?.delivered > 0 && (invoiceStats as any).data?.invoices?.pending > 0) {
          steps.push({
            id: 'create-invoice',
            title: 'Create Invoices',
            description: 'Generate invoices for delivered orders',
            action: 'Create Invoice',
            route: '/enterprise/post-quote-activity/invoices',
            icon: <CreditCard className="w-5 h-5" />,
            priority: 'medium',
            status: 'pending'
          })
        }
      }

      // Sort by priority (high -> medium -> low)
      const priorityOrder = { high: 0, medium: 1, low: 2 }
      steps.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority])

      setNextSteps(steps)
    } catch (error) {
      console.error('Error loading next steps:', error)
    } finally {
      setLoading(false)
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'border-red-200 bg-red-50'
      case 'medium': return 'border-yellow-200 bg-yellow-50'
      case 'low': return 'border-blue-200 bg-blue-50'
      default: return 'border-gray-200 bg-gray-50'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-4 h-4 text-green-500" />
      case 'in-progress': return <Clock className="w-4 h-4 text-yellow-500" />
      case 'pending': return <AlertCircle className="w-4 h-4 text-red-500" />
      default: return <Play className="w-4 h-4 text-blue-500" />
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded w-full"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          </div>
        </div>
      </div>
    )
  }

  if (nextSteps.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Next Steps</h3>
          <CheckCircle className="w-6 h-6 text-green-500" />
        </div>
        <p className="text-gray-600">
          Great job! All current tasks are up to date. Check back later for new activities.
        </p>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Next Steps</h3>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-500">Priority:</span>
          <div className="flex space-x-1">
            <div className="w-3 h-3 bg-red-400 rounded-full"></div>
            <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
            <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {nextSteps.slice(0, 3).map((step, index) => (
          <div
            key={step.id}
            className={`border-l-4 p-4 rounded-r-lg transition-all duration-200 hover:shadow-md cursor-pointer ${getPriorityColor(step.priority)}`}
            onClick={() => navigate(step.route)}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3 flex-1">
                <div className="flex-shrink-0 mt-1">
                  {step.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <h4 className="text-sm font-medium text-gray-900">{step.title}</h4>
                    {getStatusIcon(step.status)}
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{step.description}</p>
                  <button
                    className="inline-flex items-center px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
                    onClick={(e) => {
                      e.stopPropagation()
                      navigate(step.route)
                    }}
                  >
                    {step.action}
                    <ArrowRight className="w-3 h-3 ml-1" />
                  </button>
                </div>
              </div>
              {index === 0 && (
                <div className="flex-shrink-0">
                  <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                    Next
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {nextSteps.length > 3 && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <button
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            onClick={() => navigate('/enterprise/post-quote-activity/dashboard')}
          >
            View all {nextSteps.length} next steps →
          </button>
        </div>
      )}
    </div>
  )
}

export default NextStepGuide 